import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  Fade
} from "@mui/material";

// アイコン
import BuildIcon from '@mui/icons-material/Build';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * メンテナンス専用ページ
 * 
 * システムメンテナンス中に表示される独立したページ
 * Dashboard.jsx から条件によって遷移される
 */
const MaintenancePage = () => {
  return (
    <Box 
      sx={{ 
        bgcolor: '#F5F7FA', 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        <Fade in={true}>
          <Paper 
            elevation={3}
            sx={{ 
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 3,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <Stack spacing={3} alignItems="center">
              {/* メンテナンスアイコン */}
              <Box 
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  p: 3,
                  animation: 'pulse 2s infinite'
                }}
              >
                <BuildIcon sx={{ fontSize: { xs: 48, sm: 64 } }} />
              </Box>

              {/* タイトル */}
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', sm: '2.125rem' }
                }}
              >
                システムメンテナンス中
              </Typography>

              {/* メッセージ */}
              <Typography 
                variant="h6" 
                sx={{ 
                  opacity: 0.9,
                  maxWidth: '600px',
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                現在、システムのメンテナンス作業を実施しております。
                <br />
                ご不便をおかけして申し訳ございません。
              </Typography>

              {/* 詳細情報 */}
              <Paper 
                elevation={0}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  p: 3,
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  width: '100%',
                  maxWidth: '500px'
                }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AccessTimeIcon sx={{ fontSize: 20 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        メンテナンス時間
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, pl: 3 }}>
                      作業完了まで今しばらくお待ちください
                    </Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <EmailIcon sx={{ fontSize: 20 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        お問い合わせ
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, pl: 3, mb: 2 }}>
                      緊急のお問い合わせは以下のLINEからご連絡ください
                    </Typography>
                    
                    {/* LINEお問い合わせボタン */}
                    <Button
                      variant="contained"
                      href="https://lin.ee/vNwc6iT"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        bgcolor: '#06C755',
                        color: 'white',
                        fontWeight: 'bold',
                        py: 1,
                        px: 3,
                        ml: 3,
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          bgcolor: '#05B04A',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(6, 199, 85, 0.3)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      📱 LINE友達追加
                    </Button>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, textAlign: 'center' }}>
                      詳細は公式サイトをご確認ください
                    </Typography>
                    <Button
                      variant="outlined"
                      href="https://supply-assist.jp/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        mt: 1,
                        width: '100%',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      🌐 公式サイトへ
                    </Button>
                  </Box>
                </Stack>
              </Paper>

              {/* リフレッシュボタン */}
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => window.location.reload()}
                size="large"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  mt: 3,
                  minWidth: 200,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                ページを更新
              </Button>
            </Stack>
          </Paper>
        </Fade>
      </Container>

      {/* CSSアニメーション */}
      <style jsx global>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.8;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </Box>
  );
};

export default MaintenancePage;